import { mapActions, mapState } from 'pinia';
import {
  get, cloneDeep, find, keyBy, values,
} from 'lodash-es';
import chain from '@/lib/utils/chain';
import { tryFitModule } from '@/lib/utils/grid';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  computed: {
    ...mapState(useEditorStore, [
      'draft',
      'gridMatrix',
      'gridIsDisabled',
    ]),
    isSlideshow() {
      return get(this, 'draft.options.type') !== 'board';
    },
  },
  watch: {
    activeModule() {
      if (!this.activeModule || this.mode === 'VIEW') return;

      this.$nextTick(() => { // wait for DOM updates
        const modEl = this.$refs.gridItems.find((item) => item.i === this.activeModule)?.$el;

        if (modEl) {
          setTimeout(() => {
            /*
              If no element within the module is already focused, then set focus to the first
              focusable element that is not disabled, readOnly, or an action menu button.
            */
            if (!modEl.contains(document.activeElement)) {
              const getFocusable = (selector) => Array.from(modEl.querySelectorAll(selector))
                .find((el) => !el.disabled && !el.readOnly && !el.matches('.asset-module__action-menu-trigger'));
              const focusable = getFocusable('.ProseMirror, .nebula-textarea, input') || getFocusable('button');
              if (focusable) {
                focusable.focus({ preventScroll: true });
              }
            }
          }, 100);

          /*
            If the canvas already has a scrollIntoView function implemented,
            then don't try to scroll into view.
          */
          if (!this.scrollIntoView) {
            const modRect = modEl.getBoundingClientRect();
            const scrollRect = this.scrollContainer.getBoundingClientRect();

            // If module is outside visible area, scroll into view
            if (
              modRect.top < scrollRect.top || modRect.bottom > scrollRect.bottom
              || modRect.left < scrollRect.left || modRect.right > scrollRect.right
            ) {
              // The module extends past the viewport; scroll it into view. Since it's possible
              // that this might be a new module that extends the page, we have to wait
              // until after the 0.25s height/transform transitions just in case
              setTimeout(() => {
                modEl.scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                });
              }, 251);
            }
          }
        }
      });
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULES,
      types.DELETE_MODULE,
      types.CACHE_GRID_MATRIX,
      types.ADD_MODULE_CONFLICT_MESSAGE,
    ]),
    isResized(mod, layoutItem) {
      return mod.layout.width !== layoutItem.w
        || mod.layout.height !== layoutItem.h;
    },
    isMoved(mod, layoutItem) {
      return mod.layout.x_position !== layoutItem.x
        || mod.layout.y_position !== layoutItem.y;
    },
    onGridLayoutUpdated(layout, modules, canvasName, canUndo = true) {
      // Get all the modules that have been updated (resized or moved)
      const modulesToUpdate = chain(modules)
        .map((mod) => {
          const layoutItem = layout.find((item) => item.i === mod.id);
          return layoutItem ? {
            mod,
            layoutItem,
            isResized: this.isResized(mod, layoutItem),
            isMoved: this.isMoved(mod, layoutItem),
          } : null;
        })
        .filter((mod) => mod && (mod.isResized || mod.isMoved))
        .value();

      // Update modules from the grid layout and save.
      const updatedModules = modulesToUpdate.map((updated) => {
        const mod = cloneDeep(updated.mod);
        const { layoutItem } = updated;

        // Update module position and size
        mod.layout.x_position = layoutItem.x;
        mod.layout.y_position = layoutItem.y;
        mod.layout.width = layoutItem.w;
        mod.layout.height = layoutItem.h;

        return mod;
      });

      /*
        Group all module updates into a single transaction for better efficiency.
      */
      if (updatedModules.length) {
        const maxColIndex = get(this, 'gridMatrix[0].length', 12) - 1;
        const maxRowIndex = get(this, 'gridMatrix.length', 11) - 1;
        const totalColumns = maxColIndex + 1;
        const totalRows = maxRowIndex + 1;
        const totalArea = totalColumns * totalRows;
        const outOfBoundsModules = this.isSlideshow
          && !this.gridIsDisabled
          && canvasName === 'modules'
          && this.gridMatrix
          && updatedModules.filter((mod) => {
            const left = mod.layout.x_position;
            const top = mod.layout.y_position;
            const right = left + mod.layout.width - 1;
            const bottom = top + mod.layout.height - 1;
            return left < 0 || top < 0 || right > maxColIndex || bottom > maxRowIndex;
          });

        if (get(outOfBoundsModules, 'length')) {
          /*
            Build a list of modules (with layout updates applied) that does
            not include the out of bounds modules.
          */
          const inBoundsModulesById = keyBy(modules, 'id');
          const inBoundsUpdatedModulesById = keyBy(updatedModules, 'id');
          updatedModules.forEach((mod) => {
            inBoundsModulesById[mod.id] = mod;
          });
          outOfBoundsModules.forEach((mod) => {
            delete inBoundsModulesById[mod.id];
            delete inBoundsUpdatedModulesById[mod.id];
          });

          /*
            Cache the grid matrix without the out of bounds modules.
            This is so we can try to place them in free space on the grid.
          */
          const cacheGridMatrix = () => {
            this[types.CACHE_GRID_MATRIX]({
              columns: totalColumns,
              rows: totalRows,
              modules: values(inBoundsModulesById),
            });
          };
          cacheGridMatrix();

          const inBoundsUpdatedModules = values(inBoundsUpdatedModulesById);

          /*
            For each out of bounds module, scan our updated grid matrix and
            attempt to place in a different location on the grid.
          */
          outOfBoundsModules.forEach((mod, modIndex) => {
            const usedArea = this.gridMatrix.reduce((acc, row) => {
              acc.area += row.filter((value) => value === 1).length;
              return acc;
            }, { area: 0 });
            /*
              Attempt to fit all modules by splitting the unused area by the number
              of remaining modules to place and using this as the maxArea
              for a module to occupy when placed.
            */
            const maxAreaPerModule = Math.floor((totalArea - usedArea.area)
              / (outOfBoundsModules.length - modIndex));
            const fittedLayout = tryFitModule(this.gridMatrix, mod, maxAreaPerModule);
            if (fittedLayout) {
              const placedMod = {
                ...mod,
                layout: fittedLayout,
              };
              inBoundsUpdatedModules.push(placedMod);
              inBoundsModulesById[placedMod.id] = placedMod;
              // Cache the grid matrix again with the placed module
              cacheGridMatrix();
            } else {
              /*
                If the module couldn't be placed, we need to delete it and
                add a message to be displayed to the user who added the module.
              */
              this[types.DELETE_MODULE]({ canvasName, id: mod.id, canUndo: false });
              this[types.ADD_MODULE_CONFLICT_MESSAGE](mod.id);
            }
          });

          /*
            Update all the modules that changed and stayed in bounds.
          */
          if (inBoundsUpdatedModules.length) {
            this[types.UPDATE_MODULES]({
              canvasName,
              modules: inBoundsUpdatedModules,
              canUndo: false,
            });
          }
        } else {
          this[types.UPDATE_MODULES]({ canvasName, modules: updatedModules, canUndo });
        }
      }
    },
    getBeforeModuleContainerRef(id) {
      return find(this.$refs?.gridItemBeforeContainers,
        (el) => el.getAttribute('id') === `grid-item-before-container-${id}`);
    },
    getAfterModuleContainerRef(id) {
      return find(this.$refs?.gridItemAfterContainers,
        (el) => el.getAttribute('id') === `grid-item-after-container-${id}`);
    },
    onGridElementClicked(target) {
      if (!this.$refs.gridLayout || !this.$refs.gridItems || target.closest('.modal-manager')) return;
      const { gridItems } = this.$refs;
      const changes = {};

      /** ===== Collaborative Editing ===== */
      gridItems.forEach((gridItem) => {
        const moduleId = gridItem.i;
        const currentlyEditingUser = this.currentlyEditing(moduleId);

        // Get menus showing for this grid item
        let gridItemMenusContainTarget = false;
        if (this.menuContainer) {
          const menus = this.menuContainer.querySelectorAll(`[editor-floating-menu-module-id='${moduleId}']`);
          if (menus.length && Array.from(menus).find((menu) => menu.contains(target))) {
            gridItemMenusContainTarget = true;
          }
        }

        const gridItemContainsTarget = gridItem.$el.contains(target) || gridItemMenusContainTarget;

        // module was not selected
        if (!gridItemContainsTarget) {
          if (currentlyEditingUser && currentlyEditingUser.id === this.connectedUser?.id) {
            changes[moduleId] = null;
          }
        // Module was selected and no one is editing it
        } else if (!currentlyEditingUser) {
          changes[moduleId] = this.connectedUser?.id;
        }
      });
      if (this.mode === 'EDIT') this[types.UPDATE_CURRENTLY_EDITING](changes);
      /** ===== End collaborative editing ===== */

      const menuContextContainsTarget = this.menuContainer && this.menuContainer.contains(target);
      const gridLayoutContainsTarget = this.$refs.gridLayout.$el.contains(target);

      if (menuContextContainsTarget || gridLayoutContainsTarget) {
        let moduleId = null;
        if (gridLayoutContainsTarget) {
          const clickedItem = this.$refs.gridItems.find(({ $el }) => $el.contains(target));
          moduleId = clickedItem ? clickedItem.i : null;
        }
        if (!moduleId && menuContextContainsTarget) {
          const menu = target.closest('[editor-floating-menu-module-id]');
          if (menu) {
            moduleId = menu.getAttribute('editor-floating-menu-module-id');
          }
        }

        const currentlyEditingUser = this.currentlyEditing(moduleId);

        // ====== Collaborative Editing ===== //
        // Module is selected and there's someone already using it
        if (currentlyEditingUser && currentlyEditingUser.id !== this.connectedUser?.id) {
          this.activeModule = null;
          return;
        }
        // ====== End collaborative Editing ===== //

        this.activeModule = moduleId;
      } else {
        this.activeModule = null;
      }
    },
  },
};
